import { useRef } from 'react';

import { useLazyFetchGraphql } from '@zoopla/fetch-graphql';
import { SigninUserInput } from '../../../graphql.generated';
import {
    SigninUserV2Document,
    SigninUserV2Mutation,
    SigninUserV2MutationVariables
} from '../queries/signIn.generated';
import { getNextBestAction } from '../utils/getNextBestAction';
import {
    logUserAuthFailure,
    logNetworkAuthFailure,
    trackAuthFailure,
    trackAuthSuccess,
    trackAuthRememberMe
} from './authMutationHook.tracking';
import { AuthMutationHook } from './authMutationHook.types';
import { useAuth } from './useAuth';

export const useSignIn: AuthMutationHook<SigninUserInput> = ({
    trackingData
}) => {
    const hasNetworkError = useRef(false);
    const { setCurrentSession } = useAuth();
    const { lazyFetch: signInMutation } = useLazyFetchGraphql<
        SigninUserV2Mutation,
        SigninUserV2MutationVariables
    >(SigninUserV2Document, {
        onError(error) {
            if (error && 'isNetworkError' in error) {
                hasNetworkError.current = true;
            }
        }
    });

    const trackError = () => {
        trackAuthFailure({
            source: 'zpg',
            type: 'signin',
            label: trackingData.label,
            segmentData: trackingData.segmentData
        });
    };

    const trackAndThrowError = (message: string, requestId?: string) => {
        trackError();

        const requestIdMessage = requestId ? ` (Request ID: ${requestId})` : '';

        throw new Error(`${message}${requestIdMessage}`);
    };

    const authenticate = async (signInInput: SigninUserInput) => {
        const response = await signInMutation({ input: signInInput });

        if (hasNetworkError.current) {
            logNetworkAuthFailure({ type: 'signin' });
            return trackAndThrowError(
                'Sorry, something went wrong. Please try again. (Network error)'
            );
        }

        if (!response.data || response.errors) {
            return trackAndThrowError(
                'Sorry, something went wrong. Please try again.',
                response.extensions?.requestId
            );
        }

        const { data } = response;

        if (data.signinUserV2?.__typename === 'AuthError') {
            if (data.signinUserV2.errorCode === 'PASSWORD') {
                return trackAndThrowError(
                    'Incorrect email or password. Please check and try again'
                );
            }

            return trackAndThrowError(
                'Sorry, something went wrong. Please try again.',
                response.extensions?.requestId
            );
        }

        if (!data.signinUserV2?.user) {
            return trackAndThrowError(
                'Sorry, something went wrong. Please try again.',
                response.extensions?.requestId
            );
        }

        // This covers the case we have seen in a few incidents with sessions
        // when the authentication API works well but the user one fails
        if (
            !data.signinUserV2.user.loggedIn ||
            !data.signinUserV2.user.userUuid
        ) {
            logUserAuthFailure({
                type: 'signin',
                subtype: !data.signinUserV2.user.userUuid
                    ? 'no_uuid'
                    : 'not_logged_in'
            });

            return trackAndThrowError(
                'Sorry, something went wrong. Please try again later.',
                response.extensions?.requestId
            );
        }

        const {
            signinUserV2: { user }
        } = data;

        trackAuthSuccess({
            source: 'zpg',
            type: 'signin',
            userObject: user,
            label: trackingData.label,
            segmentData: trackingData.segmentData
        });

        if (signInInput?.rememberMe) {
            trackAuthRememberMe({
                source: 'zpg',
                type: 'signin',
                userObject: user,
                label: trackingData.label,
                segmentData: trackingData.segmentData
            });
        }

        setCurrentSession(user, false);

        const nextBestActions = getNextBestAction(user);

        return { ...user, actions: nextBestActions };
    };

    return authenticate;
};
